import { ArrowRight, Play, X } from 'lucide-react';
import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardDescription, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';

export interface Feature {
  text: string;
  icon: React.ComponentType<{ className?: string; size?: number }>;
  linkTo?: {
    tab: string;
  };
}

export interface FeatureModalConfig {
  title: string;
  description: string;
  features: readonly Feature[] | Feature[];
  videoSrc?: string;
  thumbnailSrc?: string;
  thumbnailText?: string;
  buttonText: string;
  onButtonClick?: () => void;
  onFeatureClick?: (tab: string) => void;
  featureType?: 'team' | 'player' | 'both';
}

interface FeatureModalProps {
  config: FeatureModalConfig;
  isOpen?: boolean;
  onClose?: () => void;
  closable?: boolean;
  variant?: 'dialog' | 'overlay';
}

const getFeatureTypeBadge = (featureType?: 'team' | 'player' | 'both') => {
  if (!featureType) return null;

  const badgeConfig = {
    team: {
      text: 'Advanced Team plan',
    },
    player: {
      text: 'Individual plan',
    },
    both: {
      text: 'Individual & Advanced Team plan',
    },
  };

  const config = badgeConfig[featureType];

  return (
    <Badge className="bg-card rounded-sm px-6 py-2 border-2 border-border font-medium text-base">
      {config.text}
    </Badge>
  );
};

const VideoComponent = ({
  videoSrc,
  autoplay = false,
}: {
  videoSrc: string;
  autoplay?: boolean;
}) => {
  // Remove existing autoplay param, then add it back only if autoplay=true
  const getFinalSrc = (src: string, shouldAutoplay: boolean) => {
    try {
      const url = new URL(src);
      url.searchParams.delete('autoplay');
      if (shouldAutoplay) url.searchParams.set('autoplay', '1');
      return url.toString();
    } catch {
      // Handle relative/non-absolute URLs
      const [base, query = ''] = src.split('?');
      const params = new URLSearchParams(query);
      params.delete('autoplay');
      if (shouldAutoplay) params.set('autoplay', '1');
      const qs = params.toString();
      return qs ? `${base}?${qs}` : base;
    }
  };

  const finalSrc = getFinalSrc(videoSrc, autoplay);

  return (
    <div className="relative aspect-video w-full rounded-lg overflow-hidden">
      <div className="relative isolate z-1 size-full overflow-hidden">
        <iframe
          key={finalSrc} // force remount if src changes
          title="Video"
          src={finalSrc}
          className="size-full"
          allowFullScreen
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        />
      </div>
    </div>
  );
};

const VideoThumbnail = ({
  thumbnailSrc,
  thumbnailText,
  onPlay,
  featureType,
}: {
  thumbnailSrc: string;
  thumbnailText?: string;
  onPlay?: () => void;
  featureType?: 'team' | 'player' | 'both';
}) => {
  const isClickable = Boolean(onPlay);
  return (
    <div
      className={`relative aspect-video w-full rounded-lg overflow-hidden ${isClickable ? 'group cursor-pointer' : ''}`}
      onClick={onPlay}
    >
      <img
        src={thumbnailSrc}
        alt="Video thumbnail"
        className={`w-full h-full object-cover transition-transform duration-300 ${isClickable ? 'group-hover:scale-105' : ''}`}
      />
      <div className="pointer-events-none absolute inset-0 rounded-lg shadow-[inset_0_4px_40px_10px_rgba(0,0,0,0.35)]" />
      <div className="absolute bottom-0 left-0 right-0 p-6 flex items-center justify-between">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isClickable && <Play className="h-5 w-5 text-primary" />}
            <span className="font-medium text-white relative text-xl">
              {thumbnailText}
              <span
                className={`absolute bottom-0 left-0 right-0 h-0.5 bg-white transition-opacity duration-300 ${isClickable ? 'opacity-0 group-hover:opacity-100' : 'opacity-0'}`}
              />
            </span>
          </div>
        </div>
        {getFeatureTypeBadge(featureType)}
      </div>
    </div>
  );
};

const FeaturesList = ({
  features,
  onFeatureClick,
}: {
  features: readonly Feature[] | Feature[];
  onFeatureClick?: (tab: string) => void;
}) => (
  <div className="space-y-3">
    <div className="text-muted-foreground font-medium text-sm lg:text-base">
      Included features
    </div>
    <div className="space-y-2">
      {features.map((feature, index) => (
        <div
          key={index}
          className={`flex items-center gap-3 ${feature.linkTo && onFeatureClick ? 'group cursor-pointer' : ''}`}
          onClick={() => {
            if (feature.linkTo && onFeatureClick) {
              onFeatureClick(feature.linkTo.tab);
            }
          }}
        >
          <feature.icon className="flex-shrink-0 text-[#405371]" size={15} />
          <p className="text-xs lg:text-sm">{feature.text}</p>
          {feature.linkTo && (
            <ArrowRight className="size-5 ml-auto text-border group-hover:text-white group-hover:-translate-x-0.5 -ms-1 transition-all" />
          )}
        </div>
      ))}
    </div>
  </div>
);

const ModalContent = ({
  config,
  onButtonClick,
  isVideoPlaying,
  onVideoPlay,
}: {
  config: FeatureModalConfig;
  onButtonClick: () => void;
  isVideoPlaying: boolean;
  onVideoPlay: () => void;
}) => (
  <div className="flex flex-col lg:flex-row lg:gap-12 gap-6 h-full lg:py-3 px-6 lg:px-12">
    <div className="lg:flex-1 flex flex-col justify-between overflow-y-auto lg:max-w-sm lg:py-5 order-1 lg:order-1">
      <div className="space-y-4 lg:space-y-6">
        <div>
          <CardTitle className="text-xl lg:text-3xl mb-2">
            {config.title}
          </CardTitle>
          <CardDescription className="leading-relaxed text-sm lg:text-base text-white">
            {config.description}
          </CardDescription>
        </div>

        {(config.videoSrc || config.thumbnailSrc) && (
          <div className="lg:hidden">
            {config.videoSrc ? (
              config.thumbnailSrc && !isVideoPlaying ? (
                <VideoThumbnail
                  thumbnailSrc={config.thumbnailSrc}
                  thumbnailText={config.thumbnailText}
                  onPlay={onVideoPlay}
                  featureType={config.featureType}
                />
              ) : (
                <VideoComponent videoSrc={config.videoSrc} autoplay={false} />
              )
            ) : (
              config.thumbnailSrc && (
                <VideoThumbnail
                  thumbnailSrc={config.thumbnailSrc}
                  thumbnailText={config.thumbnailText}
                  featureType={config.featureType}
                />
              )
            )}
          </div>
        )}

        <Separator />

        <FeaturesList
          features={config.features}
          onFeatureClick={config.onFeatureClick}
        />
      </div>

      <div className="pt-4">
        <Button className="w-full" onClick={onButtonClick}>
          {config.buttonText}
        </Button>
      </div>
    </div>

    {(config.videoSrc || config.thumbnailSrc) && (
      <div className="hidden lg:flex lg:flex-1 min-h-[200px] lg:min-h-0 order-2 lg:order-2">
        <div className="relative aspect-video w-full rounded-lg overflow-hidden h-full">
          {config.videoSrc ? (
            config.thumbnailSrc && !isVideoPlaying ? (
              <VideoThumbnail
                thumbnailSrc={config.thumbnailSrc}
                thumbnailText={config.thumbnailText}
                onPlay={onVideoPlay}
                featureType={config.featureType}
              />
            ) : (
              <VideoComponent videoSrc={config.videoSrc} autoplay={true} />
            )
          ) : (
            config.thumbnailSrc && (
              <VideoThumbnail
                thumbnailSrc={config.thumbnailSrc}
                thumbnailText={config.thumbnailText}
                featureType={config.featureType}
              />
            )
          )}
        </div>
      </div>
    )}
  </div>
);

const DialogContentComponent = ({
  config,
  onButtonClick,
  isVideoPlaying,
  onVideoPlay,
}: {
  config: FeatureModalConfig;
  onButtonClick: () => void;
  isVideoPlaying: boolean;
  onVideoPlay: () => void;
}) => (
  <div className="flex flex-col lg:flex-row lg:gap-12 gap-6 h-full lg:py-6">
    <div className="lg:flex-1 flex flex-col justify-between overflow-y-auto lg:max-w-sm sm:min-w-sm lg:py-5 order-1 lg:order-1">
      <div className="space-y-4 lg:space-y-6">
        <DialogHeader>
          <DialogTitle className="text-pretty text-xl lg:text-3xl">
            {config.title}
          </DialogTitle>
          <DialogDescription className="leading-relaxed text-sm lg:text-base text-white">
            {config.description}
          </DialogDescription>
        </DialogHeader>

        {(config.videoSrc || config.thumbnailSrc) && (
          <div className="lg:hidden">
            {config.videoSrc ? (
              config.thumbnailSrc && !isVideoPlaying ? (
                <VideoThumbnail
                  thumbnailSrc={config.thumbnailSrc}
                  thumbnailText={config.thumbnailText}
                  onPlay={onVideoPlay}
                  featureType={config.featureType}
                />
              ) : (
                <VideoComponent videoSrc={config.videoSrc} autoplay={false} />
              )
            ) : (
              config.thumbnailSrc && (
                <VideoThumbnail
                  thumbnailSrc={config.thumbnailSrc}
                  thumbnailText={config.thumbnailText}
                  featureType={config.featureType}
                />
              )
            )}
          </div>
        )}

        <Separator />

        <FeaturesList
          features={config.features}
          onFeatureClick={config.onFeatureClick}
        />
      </div>

      <DialogFooter className="pt-4">
        <Button className="w-full h-10" onClick={onButtonClick}>
          {config.buttonText} <ArrowRight className="w-4 h-4" />
        </Button>
      </DialogFooter>
    </div>

    {(config.videoSrc || config.thumbnailSrc) && (
      <div className="hidden lg:flex lg:flex-1 lg:min-h-0 order-2 lg:order-2">
        <div className="relative xl:aspect-video w-full rounded-lg overflow-hidden h-full">
          {config.videoSrc ? (
            config.thumbnailSrc && !isVideoPlaying ? (
              <VideoThumbnail
                thumbnailSrc={config.thumbnailSrc}
                thumbnailText={config.thumbnailText}
                onPlay={onVideoPlay}
                featureType={config.featureType}
              />
            ) : (
              <VideoComponent videoSrc={config.videoSrc} autoplay={true} />
            )
          ) : (
            config.thumbnailSrc && (
              <VideoThumbnail
                thumbnailSrc={config.thumbnailSrc}
                thumbnailText={config.thumbnailText}
                featureType={config.featureType}
              />
            )
          )}
        </div>
      </div>
    )}
  </div>
);

export const FeatureModal = ({
  config,
  isOpen = true,
  onClose,
  closable = false,
  variant = 'dialog',
}: FeatureModalProps) => {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  if (closable && !isOpen) return null;

  const handleButtonClick = () => {
    if (config.onButtonClick) {
      config.onButtonClick();
    }
  };

  const handleVideoPlay = () => {
    setIsVideoPlaying(true);
  };

  if (variant === 'dialog') {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="w-full sm:max-w-7xl sm:gap-6 lg:px-12">
          <DialogContentComponent
            config={config}
            onButtonClick={handleButtonClick}
            isVideoPlaying={isVideoPlaying}
            onVideoPlay={handleVideoPlay}
          />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <div className="absolute left-0 top-0 flex h-[calc(100vh-64px)] p-6 max-h-full w-full items-center justify-center overflow-hidden bg-background/80 backdrop-blur-sm">
      <Card className="relative w-full max-w-6xl border-none">
        {closable && onClose && (
          <button
            onClick={onClose}
            className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </button>
        )}
        <ModalContent
          config={config}
          onButtonClick={handleButtonClick}
          isVideoPlaying={isVideoPlaying}
          onVideoPlay={handleVideoPlay}
        />
      </Card>
    </div>
  );
};
